import { useEffect, useState } from "react";
import { api, formatBirth, type AppConfig, type ReadingResponse, type UserView } from "../api";
import { IconChevron } from "../icons";
import { openTelegramLink } from "../platform";
import { LockedList, Notes, Overlay, SectionBlock, Spinner } from "../ui";
import type { ForecastPeriod } from "./Home";

const PERIODS: { id: ForecastPeriod; label: string }[] = [
  { id: "today", label: "Сегодня" },
  { id: "tomorrow", label: "Завтра" },
  { id: "month", label: "Месяц" },
  { id: "year", label: "Год" },
];
const MONTHS = ["Январь", "Февраль", "Март", "Апрель", "Май", "Июнь", "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь"];

interface Props {
  user: UserView;
  config: AppConfig | null;
  initial: ForecastPeriod;
  onClose: () => void;
  onPaywall: () => void;
}

/** «Прогнозы»: вкладки Сегодня / Завтра / Месяц / Год, число периода, текст и рекомендации. */
export function Forecast({ user, config, initial, onClose, onPaywall }: Props) {
  const nowYear = new Date().getFullYear();
  const [period, setPeriod] = useState<ForecastPeriod>(initial);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(nowYear);
  const [data, setData] = useState<ReadingResponse | null>(null);
  const [failed, setFailed] = useState(false);
  const [share, setShare] = useState(false);

  useEffect(() => {
    let alive = true;
    setData(null);
    setFailed(false);
    const query = period === "month" ? { period, month, year: nowYear } : period === "year" ? { period, year } : { period };
    api.reading("forecast", query).then(
      (r) => alive && setData(r),
      () => alive && setFailed(true),
    );
    return () => {
      alive = false;
    };
  }, [period, month, year, nowYear]);

  if (share && data) return <ShareDay data={data} config={config} onClose={() => setShare(false)} />;

  const doc = data?.reading.doc;
  const label = doc ? (doc.headline.split(" — ")[0] ?? "") : "";
  const full = doc?.sections.find((s) => s.id === "full");
  const teaser = doc?.sections.find((s) => s.id === "teaser");
  const rest = doc?.sections.filter((s) => !["full", "teaser"].includes(s.id)) ?? [];

  return (
    <Overlay title="" back tone="light" onClose={onClose}>
      <div className="pagehead">
        <h1>Прогнозы</h1>
        <p>Прогнозы формируются для твоей даты рождения, указанной в профиле {formatBirth(user.birthDate)}</p>
      </div>
      <div className="tabs">
        {PERIODS.map((p) => (
          <button key={p.id} className={`tab${period === p.id ? " on" : ""}`} onClick={() => setPeriod(p.id)}>
            {p.label}
          </button>
        ))}
      </div>
      <div className="sheetbody">
        {!data && !failed && <Spinner />}
        {failed && <p className="text-block">Не удалось загрузить прогноз. Попробуйте позже.</p>}
        {data && doc && (
          <>
            {period === "month" || period === "year" ? (
              <label className="selectbtn" style={{ position: "relative", marginBottom: 12 }}>
                <span>{data.reading.title}</span>
                <IconChevron />
                {period === "month" ? (
                  <select
                    aria-label="Месяц"
                    value={month}
                    onChange={(e) => setMonth(Number(e.target.value))}
                    style={{ position: "absolute", inset: 0, opacity: 0, width: "100%" }}
                  >
                    {MONTHS.map((m, i) => (
                      <option key={m} value={i + 1}>
                        {m} {nowYear}
                      </option>
                    ))}
                  </select>
                ) : (
                  <select
                    aria-label="Год"
                    value={year}
                    onChange={(e) => setYear(Number(e.target.value))}
                    style={{ position: "absolute", inset: 0, opacity: 0, width: "100%" }}
                  >
                    {[nowYear - 1, nowYear, nowYear + 1, nowYear + 2].map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                )}
              </label>
            ) : (
              <h2>{data.reading.title}</h2>
            )}

            <div className="card" style={{ background: "var(--card)" }}>
              <div className="label">Твоё {label.replace("Число", "число")}</div>
              <h3>{doc.headline.split(": ")[1] ?? doc.headline}</h3>
              {typeof doc.numbers.number === "number" && <div className="bignum">{doc.numbers.number}</div>}
              {period === "today" && (
                <button className="outline" onClick={() => setShare(true)}>
                  Подробнее
                </button>
              )}
            </div>

            {full ? <SectionBlock section={full} heading={null} /> : teaser && <SectionBlock section={teaser} heading={null} />}
            {rest.map((s) => (
              <SectionBlock key={s.id} section={s} />
            ))}
            <LockedList doc={doc} onPaywall={onPaywall} />
            <Notes doc={doc} />
          </>
        )}
      </div>
    </Overlay>
  );
}

function ShareDay({ data, config, onClose }: { data: ReadingResponse; config: AppConfig | null; onClose: () => void }) {
  const doc = data.reading.doc;
  const teaser = doc.sections.find((s) => s.id === "teaser");
  const full = doc.sections.find((s) => s.id === "full");
  const advice = doc.sections.find((s) => s.id === "advice");
  const number = doc.numbers.number;
  const lead = full?.parts?.[0]?.text ?? teaser?.body ?? "";
  const shareText = `Моё число дня — ${String(number)}. ${lead}`;

  return (
    <Overlay title="Твоё число дня" onClose={onClose}>
      <div className="sharecard">
        <div className="bignum">{String(number)}</div>
        <h3>Твоё число дня {String(number)}</h3>
        <p>{lead}</p>
        {advice && (
          <>
            <p className="advice">СОВЕТ ДНЯ:</p>
            <p>{advice.body}</p>
          </>
        )}
      </div>
      <div className="gutter">
        <button
          className="primary"
          onClick={() => {
            const link = config?.botUsername ? `https://t.me/${config.botUsername}` : "";
            openTelegramLink(`https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(shareText)}`);
          }}
        >
          Поделиться
        </button>
      </div>
    </Overlay>
  );
}
